import { useEffect, useState } from "react";
import { getAllTestimonials, createTestimonial, updateTestimonial, deleteTestimonial, uploadSiteImage } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, Camera, Loader2 } from "lucide-react";
import { logger } from "../../lib/logger";
import { DEFAULT_EMPTY_TESTIMONIAL, EMPTY_TESTIMONIALS, TOAST_IMAGE_UPLOADED, TOAST_IMAGE_ERROR, TOAST_NO_PERMISSION, TOAST_SAVE_ERROR, TOAST_UPDATED, TOAST_REMOVED, TOAST_REMOVE_ERROR, PLH_TESTIMONIAL, TIP_EDIT, TIP_DELETE } from "../../lib/constants";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

export default function AdminTestimonials({ readOnly = false, activeLang }) {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(DEFAULT_EMPTY_TESTIMONIAL);
  const [uploading, setUploading] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  async function load() {
    const data = await getAllTestimonials(activeLang);
    setItems(data || []);
  }
  useEffect(() => { load(); }, [activeLang]);

  useEffect(() => {
    if (modal === null || itemToDelete) return;
    function onKey(e) {
      if (e.key === "Escape") { closeModal(); }
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { saveModal(); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modal, itemToDelete, form]);

  function openNew() { setForm(DEFAULT_EMPTY_TESTIMONIAL); setModal("new"); }
  function openEdit(t) { setForm({ name: t.name, role: t.role || "", quote: t.quote, avatar_url: t.avatar_url || "", published: !!t.published }); setModal(t.uid); }
  function closeModal() { setModal(null); setForm(DEFAULT_EMPTY_TESTIMONIAL); }

  async function handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadSiteImage(file);
      setForm(prev => ({ ...prev, avatar_url: url }));
      toast.success(TOAST_IMAGE_UPLOADED);
    } catch (err) {
      logger.error(err);
      toast.error(TOAST_IMAGE_ERROR);
    } finally {
      setUploading(false);
    }
  }

  function handleRemoveImage() {
    setForm(prev => ({ ...prev, avatar_url: "" }));
  }

  async function saveModal() {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    try {
      if (modal === "new") {
        await createTestimonial(form, activeLang);
      } else {
        await updateTestimonial(modal, form, activeLang);
      }
      toast.success(TOAST_UPDATED);
      closeModal();
      load();
    } catch (err) { logger.error(err); toast.error(TOAST_SAVE_ERROR); }
  }

  async function remove() {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    if (!itemToDelete) return;
    try {
      await deleteTestimonial(itemToDelete);
      toast.success(TOAST_REMOVED);
      load();
    } catch (err) {
      logger.error(err);
      toast.error(TOAST_REMOVE_ERROR);
    } finally {
      setItemToDelete(null);
    }
  }

  const isNew = modal === "new";

  return (
    <div data-id="admin-testimonials" data-testid="admin-testimonials-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="font-serif text-3xl text-softblack">Depoimentos</h2>
          <p className="text-warmgray text-sm font-light mt-1">O que os clientes dizem sobre seu trabalho.</p>
        </div>
        {!readOnly && (
          <button data-testid="testimonial-new" onClick={openNew} className="btn-primary !px-6 !py-3 !text-[11px] w-full sm:w-auto flex items-center justify-center">
            <Plus size={16} strokeWidth={1.5} className="mr-2" /> Novo Depoimento
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {items.map((t) => (
          <div key={t.uid} data-testid={`testimonial-card-${t.uid}`} className="group bg-white border border-fendi hover:shadow-md transition-all duration-300 flex flex-col relative">
            <div className="p-8 flex flex-col h-full">
              <div className="flex items-start justify-between gap-6 mb-6">
                <div className="flex gap-4 items-center">
                  {t.avatar_url ? (
                    <img src={t.avatar_url} alt={t.name} className="w-12 h-12 rounded-full object-cover border border-fendi/30" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-fendi/10 border border-fendi/20 flex items-center justify-center">
                      <span className="text-fendi font-serif text-lg">{t.name.charAt(0)}</span>
                    </div>
                  )}
                  <div>
                    <p className="font-serif text-xl text-softblack">{t.name}</p>
                    {t.role && <p className="eyebrow !text-[9px] mt-0.5 text-warmgray">{t.role}</p>}
                  </div>
                </div>
                {!readOnly && (
                  <div className="flex gap-4">
                    <button onClick={() => openEdit(t)} data-testid={`testimonial-edit-${t.uid}`} className="text-warmgray hover:text-softblack transition-colors" title={TIP_EDIT}>
                      <Pencil size={14} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => setItemToDelete(t.uid)} data-testid={`testimonial-delete-${t.uid}`} className="text-warmgray hover:text-destructive transition-colors" title={TIP_DELETE}>
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex-grow">
                <p className="text-warmgray font-light text-sm leading-relaxed italic border-l-2 border-fendi/20 pl-4 py-1">
                  "{t.quote}"
                </p>
              </div>

              <div className="mt-8 flex items-center justify-between border-t border-fendi/10 pt-4">
                <span className={`eyebrow !text-[9px] px-2 py-0.5 rounded-full ${t.published ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-warmgray/5 text-warmgray border border-warmgray/10'}`}>
                  {t.published ? "Publicado" : "Oculto"}
                </span>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full text-center text-warmgray font-light py-20 bg-fendi/5 border border-dashed border-fendi">
            {EMPTY_TESTIMONIALS}
          </div>
        )}
      </div>

      {/* Add / Edit modal */}
      <AlertDialog open={modal !== null} onOpenChange={(v) => { if (!v) closeModal(); }}>
        <AlertDialogContent className="max-w-2xl bg-white border border-fendi rounded-none" onClick={e => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-softblack">{isNew ? "Novo Depoimento" : "Editar Depoimento"}</AlertDialogTitle>
            <AlertDialogDescription className="text-warmgray text-sm font-light mt-1">
              {isNew ? "Adicione um novo depoimento de cliente." : "Altere os dados do depoimento."}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-6 py-4">
            <div className="flex justify-center">
              <div className="relative group w-24 h-24 rounded-full bg-fendi/10 overflow-hidden border border-fendi/30">
                {form.avatar_url ? (
                  <>
                    <img src={form.avatar_url} alt="preview" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 text-white z-10">
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveImage(); }} className="mb-2 hover:text-destructive transition-colors">
                        <Trash2 size={16} />
                      </button>
                      <label className="cursor-pointer flex items-center justify-center">
                        <Camera size={16} />
                        <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                      </label>
                    </div>
                  </>
                ) : (
                  <label className="w-full h-full flex items-center justify-center text-warmgray cursor-pointer">
                    <Camera size={24} strokeWidth={1} />
                    <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                  </label>
                )}
                {uploading && <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white z-20"><Loader2 className="animate-spin" /></div>}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Nome do Cliente" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="testimonial-input-name" />
              <Field label="Cargo / Empresa / Local" value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="testimonial-input-role" />
            </div>

            <div className="block">
              <span className="eyebrow mb-2 block">Depoimento</span>
              <textarea data-testid="testimonial-input-quote" rows={4} className="editorial-field w-full leading-relaxed" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} placeholder={PLH_TESTIMONIAL} />
            </div>

            <label className="flex items-center gap-3 py-2 cursor-pointer w-fit">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="testimonial-input-published" className="accent-softblack" />
              <span className="text-sm font-light text-softblack">Publicar no Site</span>
            </label>
          </div>

          <AlertDialogFooter className="mt-4 gap-3">
            <AlertDialogCancel className="btn-ghost !border-fendi/20 !py-2.5 !px-8 text-[11px] uppercase tracking-widest">Cancelar</AlertDialogCancel>
            {!readOnly && (
              <AlertDialogAction onClick={saveModal} disabled={!form.name.trim() || !form.quote.trim()}
                className="btn-primary !py-2.5 !px-8 text-[11px] uppercase tracking-widest rounded-none"
              >
                {isNew ? "Adicionar" : "Guardar"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation modal */}
      <AlertDialog open={!!itemToDelete} onOpenChange={() => setItemToDelete(null)}>
        <AlertDialogContent className="bg-white border border-fendi rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-3xl text-softblack">Eliminar Depoimento?</AlertDialogTitle>
            <AlertDialogDescription className="text-warmgray text-base font-light leading-relaxed">
              Esta ação removerá permanentemente o depoimento. Não poderá recuperar estes dados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-10 gap-4">
            <AlertDialogCancel className="btn-ghost !border-fendi/20 !py-3 !px-8 text-[11px] uppercase tracking-widest">Cancelar</AlertDialogCancel>
            {!readOnly && (
              <AlertDialogAction onClick={remove} className="bg-destructive text-white hover:bg-destructive/90 !py-3 !px-8 text-[11px] uppercase tracking-widest rounded-none">
                Confirmar Eliminação
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="eyebrow mb-3 block">{label}</span>
      <input data-testid={testid} className="editorial-field w-full" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}
